import { Link } from "react-router-dom";
import {
    Car,
    MapPin,
    Wrench,
    ShieldCheck,
    Zap,
    Cpu,
    Award,
    ArrowRight,
    Check,
    Phone,
    MessageCircle,
    Star,
    Sparkles,
    Instagram,
} from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import {
    BRAND,
    PHONES,
    SERVICES,
    DIFERENCIAIS,
    PORQUE,
    TESTIMONIALS,
    buildWhatsAppLink,
    buildTelLink,
} from "@/lib/constants";

const iconMap = { Car, MapPin, Wrench, ShieldCheck, Zap, Cpu, Award };

export default function Home() {
    const heroWhatsApp = buildWhatsAppLink(
        PHONES.aluguel.raw,
        "Olá PEG Serviços! Gostaria de solicitar um orçamento."
    );

    return (
        <div data-testid="home-page">
            {/* ========== HERO ========== */}
            <section
                data-testid="home-hero"
                className="relative bg-[#1a1a1a] text-white overflow-hidden"
            >
                {/* decorative grid */}
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "linear-gradient(#fec200 1px, transparent 1px), linear-gradient(90deg, #fec200 1px, transparent 1px)",
                        backgroundSize: "60px 60px",
                    }}
                />
                {/* yellow glow */}
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#fec200] opacity-20 blur-[120px] rounded-full" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        {/* Text column */}
                        <div className="lg:col-span-7">
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[0.95]">
                                Soluções Inteligentes para{" "}
                                <span className="bg-[#fec200] text-[#1a1a1a] px-2 inline-block">
                                    Mobilidade
                                </span>
                                , Segurança e Assistência Veicular
                            </h1>

                            <p className="mt-6 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
                                A <strong className="text-white">PEG Serviços</strong> oferece
                                soluções completas para quem busca praticidade, proteção e suporte
                                em qualquer momento. Aluguel de veículos, rastreamento veicular e
                                guincho 24 horas — sempre rápido, confiável e eficiente.
                            </p>

                            <div className="mt-9 flex flex-col sm:flex-row gap-4">
                                <a
                                    href={heroWhatsApp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-cta-orcamento"
                                    className="group inline-flex items-center justify-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-8 py-4 hover:bg-white transition-colors"
                                >
                                    Solicitar Orçamento
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <a
                                    href={heroWhatsApp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="hero-cta-whatsapp"
                                    className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-bold px-8 py-4 hover:bg-white hover:text-[#1a1a1a] transition-colors"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    Falar pelo WhatsApp
                                </a>
                            </div>

                            {/* Quick service phones */}
                            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
                                {Object.entries(PHONES).map(([key, p]) => (
                                    <a
                                        key={key}
                                        href={buildTelLink(p.raw)}
                                        data-testid={`hero-phone-${key}`}
                                        className="group bg-white/5 border border-white/10 hover:border-[#fec200] hover:bg-white/10 px-4 py-3 transition-all"
                                    >
                                        <p className="text-xs uppercase tracking-widest text-[#fec200] font-bold">
                                            {p.label}
                                        </p>
                                        <p className="text-white font-bold flex items-center gap-1.5 mt-1">
                                            <Phone className="w-3.5 h-3.5" />
                                            {p.display}
                                        </p>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Mascot column */}
                        <div className="lg:col-span-5 relative">
                            <div className="relative">
                                <div className="absolute inset-0 bg-[#fec200] translate-x-4 translate-y-4" />
                                <div className="relative bg-[#fec200] overflow-hidden">
                                    <img
                                        src={BRAND.mascot}
                                        alt="Mascote PEG Serviços"
                                        className="w-full h-auto object-contain"
                                        data-testid="hero-mascot-img"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative bg-[#fec200] text-[#1a1a1a] overflow-hidden border-t-4 border-b-4 border-[#1a1a1a]">
                    <div className="flex animate-marquee whitespace-nowrap py-3 font-display font-black text-lg">
                        {[...Array(2)].map((_, idx) => (
                            <div key={idx} className="flex items-center gap-8 px-8">
                                <span>★ ALUGUEL DE VEÍCULOS</span>
                                <span>★ RASTREAMENTO VEICULAR</span>
                                <span>★ GUINCHO 24 HORAS</span>
                                <span>★ ATENDIMENTO HUMANIZADO</span>
                                <span>★ COBERTURA EM TODA A BAHIA</span>
                                <span>★ TECNOLOGIA DE PONTA</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== SOBRE ========== */}
            <section data-testid="home-sobre" className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <SectionTitle
                                eyebrow="Sobre a PEG"
                                title="Movendo pessoas. Criando caminhos."
                                subtitle="A PEG Serviços nasceu com o propósito de oferecer soluções que facilitam a rotina de pessoas e empresas. Com atendimento humanizado, agilidade e compromisso, trabalhamos para garantir mobilidade, segurança e tranquilidade aos nossos clientes."
                            />
                            <p className="mt-5 text-base sm:text-lg text-[#4a4a4a] leading-relaxed max-w-3xl">
                                Nossa equipe está preparada para atender com excelência, oferecendo serviços modernos e eficientes que geram resultados reais.
                            </p>

                            {/* Diferenciais */}
                            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {DIFERENCIAIS.map((d) => (
                                    <div
                                        key={d}
                                        className="flex items-center gap-3 bg-white border-l-4 border-[#fec200] px-4 py-3"
                                    >
                                        <Check className="w-5 h-5 text-[#fec200] flex-shrink-0" />
                                        <span className="font-semibold text-[#1a1a1a]">{d}</span>
                                    </div>
                                ))}
                            </div>

                            <Link
                                to="/sobre"
                                data-testid="home-sobre-cta"
                                className="mt-10 inline-flex items-center gap-2 text-[#1a1a1a] font-bold border-b-4 border-[#fec200] pb-1 hover:gap-3 transition-all"
                            >
                                Conheça nossa história
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative">
                                <div className="absolute -top-4 -right-4 w-32 h-32 bg-[#fec200]" />
                                <img
                                    src="https://customer-assets.emergentagent.com/job_peg-rastreio/artifacts/0clz2bpj_ChatGPT%20Image%204%20de%20jun.%20de%202026%2C%2007_00_22.png"
                                    alt="PEG Serviços — Loja, guincho e rastreamento"
                                    className="relative w-full h-[480px] object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== SERVIÇOS ========== */}
            <section data-testid="home-servicos" className="bg-white py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Nossos Serviços"
                        title="Soluções completas para você e sua empresa"
                        subtitle="Três serviços essenciais, integrados para entregar mobilidade, segurança e suporte sempre que você precisar."
                    />

                    <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
                        {SERVICES.map((s) => {
                            const Icon = iconMap[s.icon];
                            return (
                                <div
                                    key={s.slug}
                                    data-testid={`service-card-${s.slug}`}
                                    className="group bg-[#f5f5f5] border border-transparent hover:border-[#1a1a1a] transition-all flex flex-col"
                                >
                                    <div className="relative h-52 overflow-hidden">
                                        <img
                                            src={s.image}
                                            alt={s.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/70 to-transparent" />
                                        <div className="absolute top-4 left-4 w-12 h-12 bg-[#fec200] flex items-center justify-center">
                                            <Icon className="w-6 h-6 text-[#1a1a1a]" />
                                        </div>
                                    </div>
                                    <div className="p-7 flex-1 flex flex-col">
                                        <h3 className="font-display text-2xl font-black text-[#1a1a1a]">
                                            {s.title}
                                        </h3>
                                        <p className="mt-3 text-[#4a4a4a] leading-relaxed">
                                            {s.short}
                                        </p>
                                        <ul className="mt-5 space-y-2">
                                            {s.benefits.slice(0, 4).map((b) => (
                                                <li key={b} className="flex items-start gap-2 text-sm">
                                                    <Check className="w-4 h-4 text-[#fec200] flex-shrink-0 mt-0.5" />
                                                    <span className="text-[#1a1a1a]">{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            to={s.path}
                                            data-testid={`service-card-link-${s.slug}`}
                                            className="mt-7 inline-flex items-center justify-between bg-[#1a1a1a] text-white font-bold px-5 py-3 group-hover:bg-[#fec200] group-hover:text-[#1a1a1a] transition-colors"
                                        >
                                            {s.cta}
                                            <ArrowRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ========== POR QUE PEG ========== */}
            <section data-testid="home-porque" className="bg-[#1a1a1a] text-white py-20 md:py-28 relative overflow-hidden">
                <div className="absolute -top-32 -left-32 w-[400px] h-[400px] bg-[#fec200] opacity-10 blur-[100px] rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Por que escolher a PEG"
                        title="Quatro razões para confiar na nossa equipe"
                        subtitle="Trabalhamos com transparência, agilidade e tecnologia para entregar a melhor experiência em cada serviço."
                        light
                    />

                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {PORQUE.map((p) => {
                            const Icon = iconMap[p.icon];
                            return (
                                <div
                                    key={p.title}
                                    data-testid={`porque-card-${p.title.toLowerCase()}`}
                                    className="group p-7 bg-white/5 border border-white/10 hover:border-[#fec200] hover:bg-[#fec200] hover:text-[#1a1a1a] transition-all"
                                >
                                    <div className="w-14 h-14 bg-[#fec200] group-hover:bg-[#1a1a1a] group-hover:text-[#fec200] flex items-center justify-center text-[#1a1a1a] transition-colors">
                                        <Icon className="w-7 h-7" />
                                    </div>
                                    <h3 className="font-display text-2xl font-black mt-5">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/70 group-hover:text-[#1a1a1a]/80">
                                        {p.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ========== DEPOIMENTOS ========== */}
            <section data-testid="home-depoimentos" className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Depoimentos"
                        title="O que nossos clientes dizem"
                        subtitle="Histórias reais de quem confia na PEG Serviços para mover, proteger e socorrer."
                    />

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={i}
                                data-testid={`testimonial-${i}`}
                                className="bg-white p-7 border-t-4 border-[#fec200] hover:shadow-xl transition-shadow flex flex-col"
                            >
                                <div className="flex items-center gap-1 text-[#fec200]">
                                    {[...Array(5)].map((_, j) => (
                                        <Star key={j} className="w-4 h-4 fill-current" />
                                    ))}
                                </div>
                                <p className="mt-4 text-[#1a1a1a] leading-relaxed flex-1">
                                    "{t.text}"
                                </p>
                                <div className="mt-6 pt-5 border-t border-black/10">
                                    <p className="font-bold text-[#1a1a1a]">{t.name}</p>
                                    <p className="text-sm text-[#4a4a4a]">{t.role}</p>
                                    <p className="mt-2 inline-block text-xs uppercase tracking-widest font-bold text-[#fec200]">
                                        {t.service}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ========== CHAMADA FINAL ========== */}
            <section data-testid="home-chamada-final" className="bg-[#fec200] text-[#1a1a1a] py-20 md:py-24 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: "linear-gradient(#1a1a1a 1px, transparent 1px), linear-gradient(90deg, #1a1a1a 1px, transparent 1px)",
                    backgroundSize: "60px 60px",
                }} />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold mb-5">
                        Precisa de uma solução agora?
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
                        A PEG Serviços está pronta para atender você com qualidade, segurança e compromisso.
                    </h2>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href={buildTelLink(PHONES.aluguel.raw)}
                            data-testid="cta-ligar-agora"
                            className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-bold px-8 py-4 hover:bg-white hover:text-[#1a1a1a] transition-colors"
                        >
                            <Phone className="w-5 h-5" />
                            Ligar Agora
                        </a>
                        <a
                            href={buildWhatsAppLink(PHONES.aluguel.raw, "Olá PEG! Preciso de atendimento.")}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cta-whatsapp-final"
                            className="inline-flex items-center justify-center gap-2 bg-white text-[#1a1a1a] font-bold px-8 py-4 hover:bg-[#1a1a1a] hover:text-white transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Chamar no WhatsApp
                        </a>
                    </div>
                    <a
                        href={BRAND.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="cta-instagram"
                        className="mt-8 inline-flex items-center gap-2 text-[#1a1a1a]/70 hover:text-[#1a1a1a] font-semibold"
                    >
                        <Instagram className="w-4 h-4" />
                        Siga-nos no Instagram: {BRAND.instagramHandle}
                    </a>
                </div>
            </section>
        </div>
    );
}
